import { PhoneOutlined } from "@ant-design/icons";
import { Flex, Select } from "antd";
import { FC, useCallback, useState } from "react";
import { shallowEqual } from "react-redux";
import { postUserChangeAccessRole } from "tm-shared/api";
import { UserRole } from "tm-shared/types";

import { useResetApp } from "@/hooks/app/useResetApp";
import {
    useAppDispatch,
    useAppSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import {
    StyledInfoBlock,
    StyledInfoTitle,
    StyledProfileCard,
} from "@/pages/Profile/styles";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";
import { clearSVActiveSchedule } from "@/store/slices/manager";

import { MainInformationCapabilities } from "../../MainInformation.policy";
import { StyledPhoneLink } from "./Styled";

interface UserInfoBlockProps {
    capabilities: MainInformationCapabilities;
}

export const UserInfoBlock: FC<UserInfoBlockProps> = ({ capabilities }) => {
    const [changeRoleLoading, setChangeRoleLoading] = useState(false);

    const role = useProtectedUserRoleSelector();

    const { roleName, employee, route, availableRoles } = useAppSelector(
        (s) => ({
            route: s.user.route,
            roleName: s.user.roleName,
            employee: s.user.employee,
            availableRoles: s.user.availableRoles,
        }),
        shallowEqual,
    );

    const { roles } = useAppSelector((s) => s.dictionaries);

    const dispatch = useAppDispatch();

    const { handleReset } = useResetApp();

    const handleChangeRole = useCallback(
        (value: UserRole) => {
            (async () => {
                if (capabilities.canClearActiveSchedule) {
                    dispatch(clearSVActiveSchedule());
                }

                setChangeRoleLoading(true);
                await postUserChangeAccessRole({ roleSlug: value });

                await handleReset();

                setChangeRoleLoading(false);
            })();
        },
        [capabilities.canClearActiveSchedule, dispatch, handleReset],
    );

    return (
        <StyledProfileCard>
            <Flex vertical gap={16}>
                <StyledInfoBlock $borderBottom>
                    <StyledInfoTitle>Роль</StyledInfoTitle>

                    {availableRoles.length > 1 ? (
                        <Select
                            options={availableRoles}
                            value={role}
                            onChange={handleChangeRole}
                            disabled={changeRoleLoading}
                            loading={changeRoleLoading}
                        />
                    ) : (
                        <SecondaryText>{roleName}</SecondaryText>
                    )}
                </StyledInfoBlock>

                {route && (
                    <StyledInfoBlock $borderBottom={!!employee?.parent}>
                        <StyledInfoTitle>Маршрут</StyledInfoTitle>

                        <SecondaryText>{route.name}</SecondaryText>
                    </StyledInfoBlock>
                )}

                {employee?.parent && (
                    <StyledInfoBlock>
                        <StyledInfoTitle>
                            {roles[employee.parent.role]?.name ??
                                employee.parent.role}
                        </StyledInfoTitle>

                        <PrimaryText>
                            {employee.parent.userInfo.fullName}
                        </PrimaryText>

                        <StyledPhoneLink
                            href={`tel:${employee.parent.userInfo.phone}`}
                        >
                            <PrimaryText>
                                {employee.parent.userInfo.phone}
                            </PrimaryText>

                            <PhoneOutlined />
                        </StyledPhoneLink>
                    </StyledInfoBlock>
                )}
            </Flex>
        </StyledProfileCard>
    );
};
